import type {
  KeyIndicatorEntry,
  KeyIndicatorTrend,
  KeyIndicatorsBarComponent,
  KeyIndicatorsBarContent,
} from "../../types";

/* Trend deltas — readable on white. up = green, down = red, flat = neutral grey-blue. */
const TREND_COLOR: Record<KeyIndicatorTrend, string> = {
  up: "text-[#15B07A]",
  down: "text-[#D44343]",
  flat: "text-[#6B7280]",
};

function TrendIcon({ trend, className }: { trend: KeyIndicatorTrend; className?: string }) {
  if (trend === "up") {
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden className={className}>
        <path fill="currentColor" d="M12 4l8 8h-5v8h-6v-8H4z" />
      </svg>
    );
  }
  if (trend === "down") {
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden className={className}>
        <path fill="currentColor" d="M12 20l-8-8h5V4h6v8h5z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden className={className}>
      <path fill="currentColor" d="M4 11h12l-3-3 1.4-1.4L20 12l-5.6 5.4L13 16l3-3H4z" />
    </svg>
  );
}

function IndicatorItem({ indicator }: { indicator: KeyIndicatorEntry }) {
  const trend = indicator.trend ?? "flat";
  const color = TREND_COLOR[trend];
  return (
    <li className="flex items-center gap-3 whitespace-nowrap px-6 py-2.5 text-sm">
      <span className="font-semibold uppercase tracking-wide text-[#00B862]/80">{indicator.label}</span>
      <span className="font-bold tabular-nums text-[#00B862]">
        {indicator.value}
        {indicator.unit ? <span className="ml-1 font-medium text-[#00B862]/80">{indicator.unit}</span> : null}
      </span>
      {indicator.delta ? (
        <span className={`flex items-center gap-1 font-semibold tabular-nums ${color}`}>
          <TrendIcon trend={trend} className="h-3.5 w-3.5" />
          {indicator.delta}
        </span>
      ) : null}
      <span aria-hidden className="ml-3 h-3 w-px shrink-0 bg-[#00B862]/20" />
    </li>
  );
}

/** Slot-friendly inner ticker — no full-bleed wrapper. Use this when the bar is mounted inside
 * a parent that already spans the viewport (e.g. the fixed BelowHeaderSlot). */
export function KeyIndicatorsBarTicker({ content }: { content: KeyIndicatorsBarContent }) {
  const { indicators, ariaLabel } = content;
  if (!indicators || indicators.length === 0) return null;
  // Duplicate so the marquee loops seamlessly without a visible jump.
  const trackItems = [...indicators, ...indicators];
  return (
    /* No top border — the header above already paints a bottom separator, so adding our own
     * on top doubles up to 2px (visible on mobile where the lines are thicker per density). */
    <section
      aria-label={ariaLabel ?? "Indicateurs clés"}
      className="overflow-hidden border-b border-[#00B862]/15 bg-[#EEF1F0] text-[#00B862]"
    >
      <div className="apa-key-indicators-track flex min-w-max">
        <ul className="flex" role="presentation">
          {trackItems.map((indicator, idx) => (
            <IndicatorItem key={`${indicator.label}-${idx}`} indicator={indicator} />
          ))}
        </ul>
      </div>
    </section>
  );
}

/** In-page renderer (legacy path: bar embedded in a page's component list).
 * Wraps the ticker in .apa-full-bleed so it spans the viewport from inside a centered container. */
export function KeyIndicatorsBarRenderer({ component }: { component: KeyIndicatorsBarComponent }) {
  const { content } = component;
  if (!content?.indicators?.length) return null;
  return (
    <div className="apa-full-bleed">
      <KeyIndicatorsBarTicker content={content} />
    </div>
  );
}
